import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Accordion, AccordionBody, AccordionHeader, AccordionItem, Col, Row,
} from 'reactstrap';

import { validateGuestReferStepRequest } from '@reducers/guest/GuestAction';
import LocaleService from '@services/LocaleService';
import { CustomButton } from '@shared/buttons';
import Alert from '@shared/components/alert/Alert';
import InputField from '@shared/components/input/InputField';
import { SavedIcon } from '@shared/icons';

export type GuestSender = {
  first_name: string;
  last_name: string;
  practice_name: string;
  email: string;
  phone: string;
};

type Props = {
  slug: string;
  clinic: { id: string; name: string };
  open: string;
  toggle: (id: string) => void;
  sender: GuestSender | null;
  onSaved: (sender: GuestSender) => void;
  hasAccount: boolean;
  setHasAccount: (value: boolean) => void;
  // A refusal that is not about the fields (link switched off, throttled).
  onError: (error: any) => void;
};

const i18n = LocaleService.getTranslations('referrals.guestRefer') as any;
const i18nSend = LocaleService.getTranslations('referrals.sendReferral') as any;

// Each field's copy in i18n.sender.
const LABEL_KEYS: Record<keyof GuestSender, string> = {
  first_name: 'firstName',
  last_name: 'lastName',
  practice_name: 'practiceName',
  email: 'email',
  phone: 'phone',
};

const EMPTY: GuestSender = {
  first_name: '', last_name: '', practice_name: '', email: '', phone: '',
};

/**
 * ENH-052 (plan D11): step 1 of the guest form. Where the signed-in form picks
 * Referred By and Referred To, a guest says who they are; the recipient is the
 * link's clinic. Laid out as the product's steps are, and checked on Next with
 * the submission's own rules. An email that has an account is refused here,
 * with a way to log in and send from the account instead.
 */
const GuestSenderForm = ({
  slug, clinic, open, toggle, sender, onSaved, hasAccount, setHasAccount, onError,
}: Props) => {
  const dispatch = useDispatch<any>();
  const [ formData, setFormData ] = useState<GuestSender>(sender ?? EMPTY);
  const [ errors, setErrors ] = useState<any>({});
  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    if (open === '1' && sender) setFormData(sender);
  }, [ open, sender ]);

  const handleInputChange = (label: keyof GuestSender, value: string) => {
    setFormData((prev) => ({ ...prev, [label]: value }));
    setErrors((prev: any) => ({ ...prev, [label]: '' }));
    if (label === 'email') setHasAccount(false);
  };

  const onHandleNext = async () => {
    setLoading(true);
    setErrors({});
    setHasAccount(false);
    try {
      await dispatch(validateGuestReferStepRequest(slug, 'sender', formData)).$promise;
      onSaved(formData);
      toggle('2');
    } catch (error: any) {
      const response = error?.response;
      if (response?.status === 422) {
        setErrors(response.data?.errors ?? {});
      } else if (response?.data?.error_code === 'sender_has_account') {
        setHasAccount(true);
      } else {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const field = (name: keyof GuestSender, required = true, extra: any = {}) => (
    <InputField
      id={`guest-sender-${name}`}
      name={name}
      label={i18n.sender[LABEL_KEYS[name]].label}
      placeholder={i18n.sender[LABEL_KEYS[name]].placeholder}
      type={name === 'email' ? 'email' : 'text'}
      required={required}
      value={formData[name]}
      fieldError={errors?.[name] ?? ''}
      onInputChangeHandler={(value) => handleInputChange(name, value)}
      {...extra}
    />
  );

  // Back to the clinic's profile, whose Send Referral preselects it.
  const loginUrl = `/auth/login?returnTo=${encodeURIComponent(`/profile/${clinic.id}`)}`;

  return (
    <div>
      <Accordion
        open={open}
        toggle={() => toggle('1')}
        className={classNames('mb-3', { 'sr-accordion-collapsed': open === '1' })}
      >
        <AccordionItem>
          <AccordionHeader className="sr-accordion-header" targetId="1">
            <Row className="align-items-center">
              <Col xs={3} md={1} style={{ width: '50px' }}>
                {sender && open !== '1' ? (<SavedIcon />) : (
                  <span
                    className={classNames('sr-accordion-number', {
                      'sr-accordion-number-collapsed': open === '1',
                    })}
                  >
                    1
                  </span>
                )}
              </Col>
              <Col xs={9} md={7} lg={8} xl={9}>
                <span className="sr-accordion-title">{i18n.sender.title}</span>
              </Col>
            </Row>
          </AccordionHeader>
          <AccordionBody accordionId="1">
            <Row>
              <Col xs={1} style={{ width: '50px' }} />
              <Col xs={12} lg={11}>
                <div className="mb-4">
                  <div className="mb-3 guest-refer-step-subtitle">
                    {LocaleService.parseTranslation(i18n.sender.subtitle, { ':clinic': clinic.name })}
                  </div>
                  <div className="mb-3 guest-refer-step-subtitle">
                    {i18n.sender.haveAccount}
                    {' '}
                    <a id="guest-sender-login-link" href={loginUrl}>{i18n.sender.login}</a>
                  </div>
                  <Row className="mb-2">
                    <Col md={6}>{field('first_name')}</Col>
                    <Col md={6}>{field('last_name')}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col>{field('practice_name', false)}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={6}>{field('email')}</Col>
                    <Col md={6}>
                      {field('phone', true, {
                        isMobile: true,
                        mobileCountryCode: '+1',
                        customClassName: 'custom-mobile-number-form-padding',
                      })}
                    </Col>
                  </Row>
                  {hasAccount && (
                    <div className="guest-refer-has-account">
                      <Alert color="error">
                        <span>
                          {LocaleService.parseTranslation(i18n.sender.hasAccount, { ':clinic': clinic.name })}
                        </span>
                      </Alert>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="sr-footer-btn-container">
                    {hasAccount && (
                      <CustomButton
                        id="guest-sender-login"
                        className="btn-lg sr-back-btn"
                        color="secondary"
                        onClick={() => { window.location.href = loginUrl; }}
                      >
                        {i18n.sender.login}
                      </CustomButton>
                    )}
                    <CustomButton
                      id="guest-sender-next"
                      className="btn-lg sr-next-btn"
                      color="primary"
                      onClick={onHandleNext}
                      disabled={loading}
                      loading={loading}
                    >
                      {i18nSend.button.next}
                    </CustomButton>
                  </div>
                </div>
              </Col>
            </Row>
          </AccordionBody>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default GuestSenderForm;
